import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { formatFullDate } from '@/shared/utils/dateUtils';

// Maps an application status to its Bootstrap badge styling. Kept module-level
// so it isn't rebuilt on every render. Exported so the decision-history modal
// in ModeratorMembers can badge past decisions with the same colors.
export const STATUS_BADGE = {
    pending: { bg: 'warning', className: 'text-dark', label: 'Pending' },
    accepted: { bg: 'success', className: '', label: 'Accepted' },
    rejected: { bg: 'danger', className: '', label: 'Rejected' },
    dismissed: { bg: 'secondary', className: '', label: 'Dismissed' },
};

/**
 * ApplicationCard
 *
 * Presentational card for a single moderator application. It renders the
 * applicant, their application text, and a status badge. While the application
 * is still `pending` it shows Accept / Reject buttons that call the provided
 * callbacks — all decision logic (confirmation modal, API call, state updates)
 * lives in the parent so this component stays purely presentational.
 */
export default function ApplicationCard({
    application,
    onAccept = null,
    onReject = null,
    onViewHistory = null,
}) {
    const {
        user,
        applicationText,
        status,
        submittedAt,
        applicationHistory = [],
        // All-time count of applications this member has submitted here
        // (incremented on every submit, never capped). Falls back to the
        // retained history + current pending one for older payloads.
        totalApplications = applicationHistory.length + 1,
    } = application;
    const displayName = user?.username ? `@${user.username}` : 'Unknown user';
    const badge = STATUS_BADGE[status] || STATUS_BADGE.pending;
    const showActions = status === 'pending' && (onAccept || onReject);
    // Applications submitted before the current pending one. Uses the all-time
    // total (not the capped history) so the count stays accurate even after
    // older applications are trimmed from moderatorApplications.
    const previousApplications = totalApplications - 1;

    return (
        <Card className="mb-2 shadow-sm">
            <Card.Body className="py-2 px-3">
                <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                    <div className="d-flex align-items-center gap-3">
                        {user?.profilePicture ? (
                            <img
                                src={user.profilePicture}
                                alt=""
                                className="rounded-circle object-fit-cover mod-avatar-sm"
                                width="36"
                                height="36"
                            />
                        ) : (
                            <div
                                className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white mod-avatar-sm"
                                aria-hidden="true"
                            >
                                <i className="bi bi-person-fill" />
                            </div>
                        )}
                        <div>
                            <p className="mb-0 fw-medium">{displayName}</p>
                            {submittedAt && (
                                <p className="mb-0 text-body-secondary small">
                                    Applied {formatFullDate(submittedAt)}
                                </p>
                            )}
                            <p className="mb-0 text-body-secondary small">
                                {previousApplications === 1
                                    ? '1 previous application submitted in this community'
                                    : `${previousApplications} previous applications submitted in this community`}
                            </p>
                            {applicationHistory.length > 0 && onViewHistory && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="text-dark small p-0"
                                    onClick={() => onViewHistory(application)}
                                    aria-label={`View recent applications from ${user?.username || 'this applicant'}`}
                                >
                                    View recent applications
                                </Button>
                            )}
                        </div>
                    </div>
                    <Badge bg={badge.bg} className={badge.className}>
                        {badge.label}
                    </Badge>
                </div>

                <p className="mb-0">{applicationText}</p>

                {showActions && (
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        {onReject && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => onReject(application)}
                                aria-label={`Reject application from ${user?.username || 'this applicant'}`}
                            >
                                <i
                                    className="bi bi-x-lg me-1"
                                    aria-hidden="true"
                                />
                                Reject
                            </Button>
                        )}
                        {onAccept && (
                            <Button
                                variant="success"
                                size="sm"
                                onClick={() => onAccept(application)}
                                aria-label={`Accept application from ${user?.username || 'this applicant'}`}
                            >
                                <i
                                    className="bi bi-check-lg me-1"
                                    aria-hidden="true"
                                />
                                Accept
                            </Button>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}

ApplicationCard.propTypes = {
    application: PropTypes.shape({
        applicationId: PropTypes.string,
        membershipId: PropTypes.string,
        communityId: PropTypes.string,
        user: PropTypes.shape({
            _id: PropTypes.string,
            username: PropTypes.string,
            profilePicture: PropTypes.string,
        }),
        applicationText: PropTypes.string,
        status: PropTypes.oneOf([
            'pending',
            'accepted',
            'rejected',
            'dismissed',
        ]),
        submittedAt: PropTypes.string,
        totalApplications: PropTypes.number,
        applicationHistory: PropTypes.arrayOf(
            PropTypes.shape({
                applicationId: PropTypes.string,
                status: PropTypes.oneOf(['accepted', 'rejected', 'dismissed']),
                decisionReason: PropTypes.string,
                submittedAt: PropTypes.string,
                resolvedAt: PropTypes.string,
            })
        ),
    }).isRequired,
    onAccept: PropTypes.func,
    onReject: PropTypes.func,
    onViewHistory: PropTypes.func,
};
